import { create } from "zustand";
import { RootState, INITIAL_STATE, Notification } from "../core/state-shape";
import { measure } from "@/lib/performance";
import { http } from "../lib/http";

export type NotificationsState = RootState["notifications"];

type NotificationsActions = {
  setState: (newState: NotificationsState) => void;
  patch: (p: Partial<NotificationsState>) => void;
  reset: () => void;
  fetchAll: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  archive: (ids: string[]) => Promise<void>;
  snooze: (ids: string[], until: string) => Promise<void>;
};


export const useNotificationsStore = create<NotificationsState & NotificationsActions>((set, _get) => ({
  ...INITIAL_STATE.notifications,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.notifications }),

  fetchAll: measure(async () => {
    try {
      const [activeRes, archivedRes] = await Promise.all([
        http<{ notifications: Notification[]; unreadCount: number }>('/api/notifications?filter=active'),
        http<{ notifications: Notification[]; unreadCount: number }>('/api/notifications?filter=archived')
      ]);

      const allNotifications = [...activeRes.notifications, ...archivedRes.notifications];
      allNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      const byId: Record<string, Notification> = {};
      allNotifications.forEach(n => byId[n.id] = n);

      set({
        byId,
        order: allNotifications.map(n => n.id),
        unreadCount: activeRes.unreadCount,
      });
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
  }, 'notifications.fetchAll'),

  markRead: measure(async (ids) => {
    if (ids.length === 0) return;
    await http<void>('/api/notifications/read', {
          method: 'POST',
          body: JSON.stringify({ ids })
    });
    // Let the websocket message handle the state update
  }, 'notifications.markRead'),

  archive: measure(async (ids: string[]) => {
    if (ids.length === 0) return;
    await http<void>('/api/notifications/archive', {
        method: 'POST',
        body: JSON.stringify({ ids }),
    });
    // Let the websocket message handle the state update
  }, 'notifications.archive'),

  snooze: measure(async (ids: string[], until: string) => {
    if (ids.length === 0) return;
    try {
      for (const id of ids) {
        await http<void>('/api/notifications/snooze', {
          method: 'POST',
          body: JSON.stringify({ id, until }),
        });
      }
      // Let the websocket message handle the state update
    } catch (e: any) {
      console.error("Failed to snooze notification(s)", e);
    }
  }, 'notifications.snooze'),
}));